import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Briefcase, Clock, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const JobDetailPage = () => {
  const { user } = useAuth();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [resumeFile, setResumeFile] = useState(null); // Store actual File object
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const data = await api.jobs.getOne(jobId);
      if (data) {
        setJob(data);
      } else {
        toast.error('Job not found');
        navigate('/jobs');
      }
    } catch (error) {
      // If error is 404/etc, handle it
      toast.error('Failed to load job details');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file); // Store the File object directly
    }
  };

  const handleApply = async () => {
    if (!user) {
      toast.error('Please login to apply');
      navigate('/login');
      return;
    }

    if (!coverLetter.trim()) {
      toast.error('Please write a cover letter');
      return;
    }

    if (!resumeFile) {
      toast.error('Resume is required');
      return;
    }

    setApplying(true);
    try {
      const formData = new FormData();
      formData.append('job_id', jobId);
      formData.append('cover_letter', coverLetter);
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }

      await api.applications.create(formData);

      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      setCoverLetter('');
      setResumeFile(null);
    } catch (error) {
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <div className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/jobs" className="inline-flex items-center gap-2 text-cyan-500 mb-8 hover:underline" data-testid="back-to-jobs">
            <ArrowLeft className="w-4 h-4" />
            Back to Jobs
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl p-8"
            data-testid="job-detail"
          >
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{job.title}</h1>

              <div className="flex flex-wrap gap-4 text-slate-400 mb-6">
                <span className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {job.location}
                </span>
                <span className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  {job.job_type}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-lg font-semibold">$</span>
                  {job.salary_range}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {new Date(job.created_at).toLocaleDateString()}
                </span>
              </div>

              <button
                onClick={() => setShowApplyModal(true)}
                className="btn-primary"
                data-testid="apply-now-btn"
              >
                Apply Now
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold mb-3">Job Description</h2>
                <p className="text-slate-400 leading-relaxed">{job.description}</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-3">Requirements</h2>
                <ul className="space-y-2">
                  {job.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-400">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-3">Responsibilities</h2>
                <ul className="space-y-2">
                  {job.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-400">
                      <span className="text-cyan-500 mt-1">•</span>
                      <span>{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="apply-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Apply for {job.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Cover Letter *</label>
              <Textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Tell us why you're a great fit for this role..."
                className="min-h-32 bg-slate-800 border-slate-700 text-slate-100"
                data-testid="cover-letter-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Resume *</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="w-full px-4 py-2 glass-input rounded-lg text-slate-200"
                data-testid="resume-upload"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleApply}
                disabled={applying}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="submit-application-btn"
              >
                {applying ? 'Submitting...' : 'Submit Application'}
              </Button>
              <Button
                onClick={() => setShowApplyModal(false)}
                variant="outline"
                className="border-slate-700 hover:bg-slate-800"
                data-testid="cancel-apply-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default JobDetailPage;